import { useLocation, useNavigate, NavLink as NavigationLink } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { NavLink, Spinner } from 'reactstrap';
import * as Sentry from '@sentry/react';

import { ApplicationState } from 'store/redux';
import { UserState } from 'store/redux/Authentication';
import { IAuthenticationActionCreators } from 'store/redux/Authentication';
import * as AuthenticationStore from 'store/redux/Authentication';
import Context from 'configuration/context';
import { orderServiceFactory } from 'services/orderService';
import { authenticationServiceFactory } from 'services/authenticationService';
import { IOrderReference, OrderStatus, OrderType, Pagination } from 'models/Order';
import { OrderList } from 'components';
import './Orders.css';
import { defaultOrderPagination } from 'constants/states';
import useDebounce from 'helpers/debounce';
import { getKeyByValue } from 'helpers/enum';
import { OrderFilters } from 'components/controls/OrderFilters';

export type IOrdersProps = IAuthenticationActionCreators & {
  authenticationState?: UserState;
};

const _pageSize = 30;

const Orders = function ({ setLoginState, authenticationState }: IOrdersProps) {
  const { t } = useTranslation('translation');
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const orderService = orderServiceFactory(
    Context.APIUrl + '/v1',
    authenticationServiceFactory(Context.APIUrl, { setLoginState }, authenticationState)
  );

  const [orders, setOrders] = useState<IOrderReference[]>([]);
  const [pagination, setPagination] = useState<Pagination>(defaultOrderPagination);

  const [filterStatus, setFilterStatus] = useState<OrderStatus[]>();
  const [filterType, setFilterType] = useState<OrderType[]>();
  const [filterTerm, setFilterTerm] = useState('');

  const debouncedFilterStatus = useDebounce(filterStatus, 500);
  const debouncedFilterType = useDebounce(filterType, 500);
  const debouncedFilterTerm = useDebounce(filterTerm, 500);

  const [isLoading, setIsLoading] = useState(false);
  const debouncedIsLoading = useDebounce(isLoading, 300);
  const LoginState = authenticationState?.LoginState;
  const customerId = authenticationState?.UserInRole?.selectedCustomer?.customerId;

  const resetOrders = () => {
    setOrders([]);
    setFilterTerm('');
    setPagination(defaultOrderPagination);
  };

  const fetchOrders = async (page?: number) => {
    if (!customerId) return;

    setIsLoading(true);
    const pageNumber = page ?? 1;

    const orderTypes = debouncedFilterType?.map((value) => Number(getKeyByValue(OrderType, value)));
    const orderStatuses = debouncedFilterStatus?.map((value) =>
      Number(getKeyByValue(OrderStatus, value))
    );

    try {
      const response = await orderService.getOrders(
        customerId,
        pageNumber,
        _pageSize,
        debouncedFilterTerm,
        orderTypes,
        orderStatuses
      );
      setIsLoading(false);
      const newOrdersValue =
        pageNumber === 1 ? [...response.items] : [...orders, ...response.items];

      setOrders(newOrdersValue);
      setPagination(response.pageInfo);
    } catch (error) {
      Sentry.captureException(error);
      setIsLoading(false);
      resetOrders();
    }
  };

  const onCopyClick = (order: IOrderReference) => {
    navigate(
      pathname +
        '/' +
        Object.values(OrderType)[order.orderType].toLowerCase() +
        '/' +
        order.id +
        '/copy'
    );
  };

  useEffect(() => {
    if (LoginState && LoginState === AuthenticationStore.LoginState.LOGGED_IN && customerId) {
      // Reset orders and filter when customer changes, then fetch new orders
      resetOrders();
      fetchOrders();
    }
  }, [LoginState, customerId]);

  useEffect(() => {
    setPagination(defaultOrderPagination);
    fetchOrders();
  }, [debouncedFilterTerm, debouncedFilterStatus, debouncedFilterType, t]);

  return (
    <div className="row  mt-4">
      <div className="col-12 col-xl-9">
        <div className="orders-content">
          <div className="row">
            <div className="col-12">
              <h4>{t('orders.ALL_ORDERS')}</h4>
            </div>
          </div>
          <OrderFilters
            setFilterStatus={setFilterStatus}
            setFilterType={setFilterType}
            setFilterTerm={setFilterTerm}
          />
          <div className="row">
            {debouncedIsLoading ? (
              <h1 className="loading-indicator-container col-12">
                <Spinner /> {t('orders.FETCHING_ORDERS')}...
              </h1>
            ) : (
              <div className="col-12">
                <OrderList
                  onFetchMore={() => fetchOrders(pagination.currentPage + 1)}
                  onCopyClick={onCopyClick}
                  items={orders}
                  enableFetchMore={pagination.hasNext}
                />
              </div>
            )}
          </div>
        </div>
        <span className="field-description">{t('orders.ORDER_RULES_AND_CONDITIONS')}</span>
      </div>
      <div className="col-12 col-xl-3">
        <div className="row">
          <div className="col-12">
            <h4 className="new-order-col-header">{t('orders.NEW_ORDER')}</h4>
          </div>
          <NavLink
            className="col-12 col-md-4 col-lg-4 col-xxl-6 individual-container order-link-container"
            tag={NavigationLink}
            to="/service/order/individual"
          >
            <span className="link-title">{t('orders.TYPE_Individual')}</span>
          </NavLink>
          <NavLink
            className="col-12 col-md-4 col-lg-4 col-xxl-6 group-container order-link-container"
            tag={NavigationLink}
            to="/service/order/group"
          >
            <span className="link-title">{t('orders.TYPE_Group')}</span>
          </NavLink>
          <NavLink
            className="col-12 col-md-4 col-lg-4 col-xxl-6 education-container order-link-container"
            tag={NavigationLink}
            to="/service/order/education"
          >
            <span className="link-title">{t('orders.TYPE_Education')}</span>
          </NavLink>
        </div>
      </div>
    </div>
  );
};

export default connect(
  (appState: ApplicationState) => ({ authenticationState: appState.authentication }),
  { ...AuthenticationStore.actions }
)(Orders);
